/**
 * The liveness rule (SPEC.md §5.1): one signed packet proves only that the
 * code was made at `iat`. A screen recording or a screenshot replays packets
 * that were real once. So the scanner verifies only after it has seen at
 * least MIN distinct, freshly signed packets, whose
 *
 *  - nonces are all different;
 *  - iat rise strictly, in the order the phone decoded them;
 *  - spacing matches the phone's own clock (iat_i - iat_0 ≈ seen_i - seen_0);
 *  - each was fresh on the phone's clock when it was decoded;
 *  - all arrived within WINDOW seconds.
 *
 * A recording that loops jumps back in time (iat falls) and is rejected; an
 * old recording is not fresh; a still image never yields a second packet.
 * Every packet given here must have passed its signature check first.
 */
export type LivePacket = {
  /** Issue time from the signed packet, Unix ms. */
  iat: number;
  nonce: string;
  /** When this phone decoded it, Unix ms (the phone's clock). */
  seenAt: number;
  /** What the packets must agree on: issuer, kid, page/uid. */
  subject: string;
};

export type LivenessOptions = {
  min?: number;
  windowSec?: number;
  maxAgeSec?: number;
  maxSkewSec?: number;
  /** How far the packets' spacing may differ from the phone's clock, in seconds. */
  driftSec?: number;
};

export type LivenessState = {
  state: 'collecting' | 'live' | 'replay';
  /** Distinct fresh packets in the current run. */
  count: number;
  min: number;
  /** One plain sentence. */
  message: string;
  detail: string;
};

export const LIVENESS_DEFAULTS = { min: 3, windowSec: 20, maxAgeSec: 30, maxSkewSec: 5, driftSec: 6 } as const;

export class LivenessTracker {
  readonly o: Required<LivenessOptions>;
  private run: LivePacket[] = [];
  private nonces = new Set<string>();
  /** Set once a packet broke the rule; cleared by reset(). */
  private replay: string | null = null;

  constructor(o: LivenessOptions = {}) {
    this.o = { ...LIVENESS_DEFAULTS, ...o };
  }

  reset() {
    this.run = [];
    this.nonces.clear();
    this.replay = null;
  }

  get packets(): readonly LivePacket[] {
    return this.run;
  }

  /** Adds one signature-checked packet and returns the state. */
  add(p: LivePacket): LivenessState {
    const { maxAgeSec, maxSkewSec, windowSec, driftSec } = this.o;
    if (this.nonces.has(p.nonce)) {
      // The current packet again: the stream repeats its frames. An OLDER one again: a live stream never goes back.
      if (this.run.length && this.run[this.run.length - 1].nonce === p.nonce) return this.state();
      if (this.run.some((x) => x.nonce === p.nonce)) return this.fail('an older code came back after a newer one: a recording is looping');
      return this.state();
    }
    const age = (p.seenAt - p.iat) / 1000;
    if (age > maxAgeSec) return this.fail(`a packet was signed ${age.toFixed(0)} s before this phone saw it (limit ${maxAgeSec} s)`);
    if (age < -maxSkewSec) return this.fail(`a packet was signed ${(-age).toFixed(1)} s in this phone's future: check the phone's clock`);
    const first = this.run[0];
    const last = this.run[this.run.length - 1];
    if (first && p.subject !== first.subject) {
      // Another code came into view: start over with it.
      this.reset();
      return this.add(p);
    }
    if (last) {
      if (p.iat <= last.iat) return this.fail(`packet times went backwards (${new Date(p.iat).toISOString()} after ${new Date(last.iat).toISOString()}): a recording is looping`);
      const drift = Math.abs(p.iat - first!.iat - (p.seenAt - first!.seenAt)) / 1000;
      if (drift > driftSec) return this.fail(`the packets are ${(p.iat - first!.iat) / 1000} s apart, but this phone saw them ${(p.seenAt - first!.seenAt) / 1000} s apart: not a live code`);
      if ((p.seenAt - first!.seenAt) / 1000 > windowSec) {
        // Too slow to count as one look: keep only the packets inside the window.
        this.run = this.run.filter((x) => (p.seenAt - x.seenAt) / 1000 <= windowSec);
      }
    }
    this.run.push(p);
    this.nonces.add(p.nonce);
    return this.state();
  }

  private fail(detail: string): LivenessState {
    this.replay = detail;
    this.run = [];
    return this.state();
  }

  state(): LivenessState {
    const { min } = this.o;
    if (this.replay) {
      return {
        state: 'replay',
        count: this.run.length,
        min,
        message: 'This looks like a replayed recording or a screenshot, not a live PageID code.',
        detail: this.replay,
      };
    }
    const n = this.run.length;
    if (n >= min) {
      const span = (this.run[n - 1].iat - this.run[0].iat) / 1000;
      return { state: 'live', count: n, min, message: `${n} freshly signed codes in ${span.toFixed(1)} s, in step with this phone's clock.`, detail: `nonces distinct, iat rising, spacing within ${this.o.driftSec} s of the phone's clock, window ${this.o.windowSec} s` };
    }
    return { state: 'collecting', count: n, min, message: `Checking that the code is live: ${n} of ${min} fresh codes`, detail: '' };
  }
}
